import React, { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../components/Seo";
import { Modal, PageHeader, Switch } from "../../components/lawyer/LawyerUi";
import { isDarkTheme, setDarkTheme } from "../../components/lawyer/lawyerTheme";
import { apiErrorMessage, lawyerAuth, lawyerService } from "../../services/lawyerApi";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ar", label: "العربية (Arabic)" },
];

const Row = ({ icon, label, to, href, onClick, right, danger, toggle }) => {
  const content = (
    <>
      <i className={`bi ${icon} lwp-settings-icon`} />
      <span className="lwp-settings-label">{label}</span>
      {right}
      {!danger && <i className="bi bi-chevron-right lwp-settings-chevron" />}
    </>
  );
  const cls = `lwp-settings-row${danger ? " danger" : ""}`;
  if (to) return <Link to={to} className={cls}>{content}</Link>;
  // Rows holding a switch can't be <button>s (no nested controls); the switch is the control.
  if (toggle) return <div className={cls} onClick={onClick}>{content}</div>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={cls}>{content}</a>;
  return <button type="button" className={cls} onClick={onClick}>{content}</button>;
};

const LawyerSettings = () => {
  const navigate = useNavigate();
  const { profile, setProfile, logout } = useOutletContext();
  const [dark, setDark] = useState(isDarkTheme());
  const [savingNotif, setSavingNotif] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const basePath = process.env.REACT_APP_BASE_PATH || "";
  const notificationsOn = Number(profile?.notification_enable ?? 1) === 1;
  const currentLang = profile?.app_lang || "en";

  const toggleNotifications = async () => {
    const next = !notificationsOn;
    setSavingNotif(true);
    setProfile((p) => ({ ...p, notification_enable: next ? 1 : 0 }));
    try {
      const res = await lawyerService.setNotifications(next);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      toast.success(res.data.message || "Saved");
    } catch (err) {
      setProfile((p) => ({ ...p, notification_enable: next ? 0 : 1 }));
      toast.error(apiErrorMessage(err, err?.message || "Unable to update notifications."));
    } finally {
      setSavingNotif(false);
    }
  };

  const toggleDark = () => {
    setDarkTheme(!dark);
    setDark(!dark);
  };

  const chooseLanguage = async (code) => {
    if (code === currentLang) {
      setLangOpen(false);
      return;
    }
    try {
      const res = await lawyerService.setLanguage(code);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      setProfile((p) => ({ ...p, app_lang: code }));
      toast.success(res.data.message || "Language updated");
      setLangOpen(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to update language."));
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      const res = await lawyerService.deleteAccount();
      if (!res?.data?.status) throw new Error(res?.data?.message);
      lawyerAuth.clear();
      toast.success(res.data.message || "Account deleted");
      navigate("/lawyer/login", { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to delete account."));
      setDeleting(false);
    }
  };

  return (
    <div className="lwp-page lwp-page-narrow">
      <Seo title="Settings | Legal Platform" noindex />
      <PageHeader title="Settings" />

      <div className="lwp-settings">
        <Row
          icon="bi-bell"
          label="Notifications"
          toggle
          onClick={() => !savingNotif && profile && toggleNotifications()}
          right={<Switch checked={notificationsOn} onChange={toggleNotifications} disabled={savingNotif || !profile} label="Notifications" />}
        />
        <Row
          icon="bi-palette"
          label="Dark Mode"
          toggle
          onClick={toggleDark}
          right={<Switch checked={dark} onChange={toggleDark} label="Dark mode" />}
        />
        <Row icon="bi-bank" label="Payout Method" to="/lawyer/payout-method" />
        <Row icon="bi-cash-coin" label="Total Earning" to="/lawyer/earnings" />
        <Row icon="bi-cash-stack" label="Call Pricing" to="/lawyer/call-pricing" />
        {/* Language switch hidden for now — restore this row to bring it back.
        <Row
          icon="bi-translate"
          label="Language"
          onClick={() => setLangOpen(true)}
          right={<span className="lwp-settings-value">{currentLang === "ar" ? "Arabic" : "English"}</span>}
        /> */}
        <Row icon="bi-info-circle" label="About Us" href={`${basePath}/about-legal-company`} />
        <Row icon="bi-telephone" label="Contact Us" to="/lawyer/contact" />
        <Row icon="bi-file-earmark-text" label="Terms and Conditions" href={`${basePath}/terms`} />
        <Row icon="bi-trash3" label="Delete Account" onClick={() => setDeleteOpen(true)} />
      </div>

      <button type="button" className="lwp-logout-link" onClick={() => setLogoutOpen(true)}>
        Logout
      </button>

      <Modal open={langOpen} onClose={() => setLangOpen(false)} title="Language">
        <div className="lwp-choice-list">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              className={`lwp-choice${currentLang === l.code ? " active" : ""}`}
              onClick={() => chooseLanguage(l.code)}
            >
              <span>{l.label}</span>
              {currentLang === l.code && <i className="bi bi-check-circle-fill" />}
            </button>
          ))}
        </div>
        <p className="lwp-modal-note">Used for your notifications and emails.</p>
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        title="Delete Account"
        footer={
          <div className="lwp-modal-actions">
            <button type="button" className="lwp-btn lwp-btn-outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>
              Cancel
            </button>
            <button type="button" className="lwp-btn lwp-btn-danger" onClick={deleteAccount} disabled={deleting}>
              {deleting ? <span className="spinner-border spinner-border-sm" /> : "Delete"}
            </button>
          </div>
        }
      >
        <p className="lwp-modal-text">
          This permanently deletes your Legal Platform lawyer account. You will be signed out and won&apos;t be able to
          recover your profile, chats or history.
        </p>
      </Modal>

      <Modal
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        title="Logout"
        footer={
          <div className="lwp-modal-actions">
            <button type="button" className="lwp-btn lwp-btn-outline" onClick={() => setLogoutOpen(false)}>
              Cancel
            </button>
            <button type="button" className="lwp-btn" onClick={logout}>
              Logout
            </button>
          </div>
        }
      >
        <p className="lwp-modal-text">Are you sure you want to log out?</p>
      </Modal>
    </div>
  );
};

export default LawyerSettings;
